<script setup>
import { computed, defineEmits, reactive, ref } from 'vue'
import { useStore } from 'vuex'
import { NButton, NForm, NFormItem, NInput, NModal, NUpload } from 'naive-ui'
import { CreateWhiteLabelForCompany } from '@/mixins/use-white-label-for-company'
import useActionStatus from '@/mixins/use-action-status'

const emit = defineEmits(['closeMainModal', 'actionCompleted'])

const { status } = useActionStatus()

const store = useStore()
const formRef = ref(null)

const model = reactive({
  companyName: null,
  phoneNumber: null,
  emailAddress: null,
  ipAddress: null,
  webhookUrl: null,
  companyLogo: null
})

const rules = {
  companyName: [
    {
      required: true,
      message: 'Please input company name',
      trigger: 'input'
    }
  ],
  phoneNumber: [
    {
      required: true,
      message: 'Please input phone number',
      trigger: 'input'
    },
    {
      validator: (_, value) => {
        const phoneRegex = /^\d{11}$/

        return phoneRegex.test(value)
      },
      message: 'Invalid phone number',
      trigger: 'input'
    }
  ],
  emailAddress: [
    {
      required: true,
      message: 'Please input email address',
      trigger: 'input'
    },
    {
      validator: (_, value) => {
        const emailRegex = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/

        return emailRegex.test(value)
      },
      message: 'Invalid email address',
      trigger: 'input'
    }
  ],
  ipAddress: [
    {
      required: true,
      message: 'Please input IP address',
      trigger: 'input'
    },
    {
      validator: (_, value) => {
        const ipAddressRegex = /^(?:(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.){3}(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)(?:,\s*(?:(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.){3}(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?))*$/

        return ipAddressRegex.test(value)
      },
      message: 'Invalid IP address',
      trigger: 'input'
    }
  ],
  webhookUrl: [
    {
      required: true,
      message: 'Please input webhook URL',
      trigger: 'input'
    },
    {
      validator: (_, value) => {
        if (!value) return false

        try {
          const url = new URL(value)

          return !!url
        } catch {
          return false
        }
      },
      message: 'Invalid webhook URL',
      trigger: 'input'
    }
  ],
  companyLogo: [
    {
      validator: () => !!model.companyLogo,
      message: 'Please upload company logo',
      trigger: 'input'
    }
  ]
}

const isLoading = ref(false)

// response modal
const responseModalDialogueOptions = reactive({
  show: false,
  actionStatus: computed(() => store.getters._actionStatus),
  actionMessage: computed(() => store.getters._actionMessage)
})

const beforeFileUpload = async ({ file, fileList }) => {
  const ALLOWED_IMAGE_EXTENSIONS = /(\.jpg|\.jpeg|\.png|\.gif|\.bmp|\.webp|\.svg)$/i
  const MAX_FILE_SIZE_MB = 2
  const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024

  try {
    if (!ALLOWED_IMAGE_EXTENSIONS.test(file.name)) {
      status('error', 'Image format not supported. Please upload JPG, PNG, GIF, BMP, WebP, or SVG files.')
      responseModalDialogueOptions.show = true

      return false
    }

    // Validate file size
    if (file.file.size > MAX_FILE_SIZE_BYTES) {
      status('error', `File size shouldn't exceed ${MAX_FILE_SIZE_MB}MB.`)
      responseModalDialogueOptions.show = true

      return false
    }

    // Clear previous file if new one is being uploaded
    if (fileList && fileList.length > 0) {
      fileList.length = 0 // Clear array completely
      // OR: fileList.splice(0, fileList.length) // Alternative method
    }

    // If all validations pass, update the model
    model.companyLogo = file.file

    return true // Explicit return for success case
  } catch (error) {
    console.error('Error during file upload validation:', error)

    status('error', 'An unexpected error occurred during file validation.')
    responseModalDialogueOptions.show = true

    return false
  }
}

const removeDocument = (_, fileList) => {
  fileList = [] // Clear array completely
  model.companyLogo = null
}

const clearFormInputs = () => {
  // clear form inputs
  formRef.value.resetFields()
}

const submitForm = async () => {
  // validate form
  await formRef.value.validate()
    .then(() => {
      // toggle spinner
      store.commit('LOADING', true)
      const formData = new FormData()

      formData.append('CompanyName', model.companyName)
      formData.append('PhoneNumber', model.phoneNumber)
      formData.append('EmailAddress', model.emailAddress)
      formData.append('IpAddress', model.ipAddress)
      formData.append('WebHookURL', model.webhookUrl)
      formData.append('CompanyLogo', model.companyLogo)

      // update benefciciary account
      CreateWhiteLabelForCompany({
        payload: formData,
        paginationParams: {
          searchValue: 'all',
          pageNumber: 1,
          pageSize: 10
        }
      })
        .then((response) => {
          // successful creation
          if ([0, '0', '00'].includes(response?.responseCode)) {
            // emit event for successful update
            emit('actionCompleted')

            // clear form inputs
            clearFormInputs()
          } else {
            // show response modal
            responseModalDialogueOptions.show = true
          }
          // ----------------------------------
        })
        .catch(() => {
          responseModalDialogueOptions.show = true
        })
        .finally(() => {
          // toggle spinner
          store.commit('LOADING', false)
        })
    })
}
</script>

<template>
  <div class="xpress-modal custom-form-modal">
    <div>
      <section class="modal-header-container">
        <h5>Create API Merchant</h5>
      </section>

      <section class="modal-content-container">
        <div class="xpress-form-group-row">
          <n-form
            ref="formRef"
            :label-width="80"
            :model="model"
            :rules="rules"
            size="large"
          >
            <n-form-item
              label="Company Name"
              path="companyName"
            >
              <n-input
                v-model:value="model.companyName"
                placeholder=""
              />
            </n-form-item>

            <n-form-item
              label="Company Email Address"
              path="emailAddress"
            >
              <n-input
                v-model:value="model.emailAddress"
                placeholder=""
              />
            </n-form-item>

            <n-form-item
              label="Company Phone Number"
              path="phoneNumber"
            >
              <n-input
                v-model:value="model.phoneNumber"
                maxlength="11"
                placeholder=""
                :allow-input="(value) => !value || /^\d+$/.test(value)"
              />
            </n-form-item>

            <n-form-item
              label="IP Address"
              path="ipAddress"
            >
              <n-input
                v-model:value="model.ipAddress"
                placeholder=""
              />
            </n-form-item>

            <n-form-item
              label="Webhook URL"
              path="webhookUrl"
            >
              <n-input
                v-model:value="model.webhookUrl"
                placeholder=""
              />
            </n-form-item>

            <n-form-item
              label="Company Logo"
              path="companyLogo"
            >
              <n-upload
                :max="1"
                @before-upload="beforeFileUpload"
                @remove="removeDocument"
              >
                <n-button>
                  <icon icon="uil:upload" /> &nbsp; Upload Image File
                </n-button>
              </n-upload>
            </n-form-item>
          </n-form>
        </div>
      </section>

      <section class="modal-action-btn-container">
        <div class="action-btn-rows">
          <button
            class="action-btn action-close"
            @click="$emit('closeMainModal')"
            type="button"
            :disabled="isLoading"
          >Close</button>
          <button
            class="action-btn action-submit"
            type="button"
            :disabled="isLoading"
            @click="submitForm"
          >Submit</button>
        </div>
      </section>
    </div>
  </div>

  <!-- response modal -->
  <n-modal
    v-model:show="responseModalDialogueOptions.show"
    :mask-closable="false"
    :closable="false"
    :show-icon="false"
    preset="dialog"
    negative-text="Close"
  >
    <span :class="[
      responseModalDialogueOptions.actionStatus==='success'?'text-success':'text-danger'
    ]">
      {{responseModalDialogueOptions.actionMessage}}
    </span>
  </n-modal>
</template>

<style lang="scss" scoped>
div.xpress-modal {
  &.custom-form-modal {
    width: 420px;

    // button style
    .action-btn {
      width: fit-content;
      display: flex;
      flex-direction: row;
      justify-content: center;
      align-items: center;
      border: transparent;
      box-sizing: border-box;
      border-radius: 5px;
      padding: 9px 20px;
      transition: all 0.4s;
      text-decoration: none;
      background-color: transparent;
      cursor: pointer;

      &:hover {
        filter: brightness(0.97);
      }
      &:active {
        filter: brightness(1);
      }
      &:focus {
        outline: transparent;
      }

      // disabled action button
      &:disabled {
        background-color: rgb(190, 190, 190) !important;
        filter: none;
        cursor: not-allowed;
      }
    }
    // -----------------------------

    & > div {
      display: flex;
      flex-direction: column;
      row-gap: 15px;
      border: 0.5px solid transparent;
      background-color: #fffefe;

      &>section {
        background-color: $xpress-light-green;

        // modal header container
        &.modal-header-container {
          border-radius: 5px 5px 0 0;
          padding: 15px 10px;

          h5 {
            text-transform: uppercase;
            font-size: 17px;
            color: #ffffff;
          }
        }

        // modal content container
        &.modal-content-container {
          display: flex;
          flex-direction: column;
          row-gap: 20px;
          padding: 10px 10px;
          color: #575f6e;
          background-color: transparent;

          // order allocation form
          div.xpress-form-group-row {
            display: flex;
            flex-direction: column;
            row-gap: 15px;

            &>div.xpress-form-group {
              display: flex;
              flex-flow: column wrap;
              row-gap: 8px;

              // input title
              &>label {
                font-style: normal;
                font-weight: 500;
                font-size: 16px;
                line-height: 19px;
                color: #575F6E;
              }
              // input
              &>input,
              &>select {
                height: 39px;
                display: flex;
                align-items: center;
                border: 0.5px solid $form-input-grey-border;
                border-radius: 5px;
                padding-top: 4px;
                padding-bottom: 4px;
                padding-left: 10px;
                padding-right: 10px;
                transition: border 0.2s;
                line-height: 14px;
                font-size: 16px;
                background-color: transparent;

                &:focus {
                  outline: none;
                  border: 2px solid $xpress-light-green;
                  box-shadow: none;
                }

                &:disabled {
                  background-color: #f8f8f8;
                }

                &.needs-validation {
                  border: 0.5px solid $form-validation-red-border;
                }
              }

              // select dropdown
              &>select {
                // dropdown options
                &>option {
                  width: 100%;
                  font-style: normal;
                  font-weight: normal;
                  font-size: 15px;
                  background-color: #fff;
                  color: $xpress-light-green;
                }
              }
            }
          }
        }

        // modal action button container
        &.modal-action-btn-container {
          padding: 15px 10px;

          & > div.action-btn-rows {
            display: flex;
            flex-flow: row wrap-reverse;
            justify-content: flex-end;
            align-items: center;
            row-gap: 5px;
            column-gap: 20px;

            & > button {
              width: 100px;

              // close
              &.action-close {
                background: #f8f8f8;
                border: 1px solid #c4c4c4;
              }
              // submit
              &.action-submit {
                background: #ffffff;
                color: $xpress-light-green;

                &:disabled {
                  color: #ffffff;
                }
              }
              // reject order button
              &.reject-order-btn {
                color: #de4f6a;
                background: #ffffff;
              }
            }
          }
        }
      }
    }
  }
}

// MEDIA QUERY

// XX-SM (<251px)
@media (max-width: 250.9px) {
  div.xpress-modal {
    &.custom-form-modal {
      width: 100%;

      & > div {
        &>section {
          // modal header container
          &.modal-header-container {
            border-radius: 0;
            padding: 15px 5px;

            h5 {
              font-size: 19px;
            }
          }

          // modal content container
          &.modal-content-container {
            // order allocation form
            div.xpress-form-group-row {
              display: flex;
              flex-direction: column;
              row-gap: 15px;

              &>div.xpress-form-group {
                display: flex;
                flex-flow: column wrap;
                row-gap: 8px;

                // input title
                &>label {
                  font-style: normal;
                  font-weight: 600;
                  font-size: 16px;
                  line-height: 19px;
                  color: #575F6E;
                }
                // input
                &>input,
                &>select {
                  height: 39px;
                  display: flex;
                  align-items: center;
                  border: 0.5px solid $form-input-grey-border;
                  border-radius: 5px;
                  padding-left: 10px;
                  padding-right: 10px;
                  transition: border 0.2s;
                  text-overflow: ellipsis;
                  font-size: 16px;
                  background-color: transparent;
                  color: #9DA1A9;

                  &:focus {
                    outline: none;
                    border: 2px solid $xpress-light-green;
                    box-shadow: none;
                  }
                }
              }
            }
          }

          // modal action button container
          &.modal-action-btn-container {
            & > div.action-btn-rows {
              & > button {
                width: 100%;
              }
            }
          }
        }
      }
    }
  }
}
// XX-SM
@media (min-width: 251px) and (max-width: 350.9px) {
  div.xpress-modal {
    &.custom-form-modal {
      width: 100%;

      & > div {
        &>section {
          // modal header container
          &.modal-header-container {
            border-radius: 0;
            padding: 15px 5px;

            h5 {
              font-size: 19px;
            }
          }

          // modal content container
          &.modal-content-container {
            // customer and order detail
            section {
              &>div.detail-row {
                div.detail-group {
                  flex-basis: 100%;
                }
              }
            }
          }
        }
      }
    }
  }
}
// X-SM
@media (min-width: 351px) and (max-width: 575.9px) {
  div.xpress-modal {
    &.custom-form-modal {
      width: 100%;

      & > div {
        &>section {
          // modal header container
          &.modal-header-container {
            border-radius: 0;

            h5 {
              font-size: 20px;
            }
          }

          // modal content container
          &.modal-content-container {
            // customer and order detail
            section {
              &>div.detail-row {
                div.detail-group {
                  flex-basis: 100%;
                }
              }
            }
          }
        }
      }
    }
  }
}
// SM
@media (min-width: 576px) and (max-width: 767.9px) {
  div.xpress-modal {
    &.custom-form-modal {
      width: 100%;

      & > div {
        &>section {
          // modal header container
          &.modal-header-container {
            border-radius: 0;
          }
        }
      }
    }
  }
}
// MD
@media (min-width: 768px) and (max-width: 991.9px) {
  div.xpress-modal {
    &.custom-form-modal {
      width: 85%;
    }
  }
}
// LG
// @media (min-width: 992px) and (max-width: 1200.9px) {}
// XL
// @media (max-width: 1201px) {}

</style>
